/**
 * Labels, defaults, input config and grading bands for the Black Friday report
 * card.
 *
 * English-only by design: the tool lives under /tools, which ships to the
 * English locales only (see EN_ONLY_SLUGS). Currency is the one locale-varying
 * piece and is resolved at render time from the request locale, not hardcoded
 * here.
 *
 * The shared margin arithmetic (contribution ratio, break-even ROAS,
 * contribution profit) lives in lib/bf-toolkit/calculations.ts and is unit
 * tested there. What stays here is the tool's own product opinion: where the
 * grade boundaries sit and how each grade is phrased. Tune the bands in one
 * place; the component reads them and never hardcodes a threshold.
 */

export const STRINGS = {
  toolTitle: "Tell us about your last Black Friday period",
  revenue: "Black Friday sale revenue",
  revenueHint:
    "Total sales across the weekend, after discounts and before returns.",
  margin: "Contribution margin",
  marginHint:
    "Margin after COGS, shipping and fees, before any discount. Only know your gross margin? Use it and read the result as a best case.",
  discount: "Average discount depth",
  discountHint: "The typical markdown you ran across the weekend.",
  adSpend: "Ad spend over the Black Friday campaign",
  adSpendHint: "Everything you spent on paid acquisition.",
  newCustomerShare: "New-customer share",
  newCustomerShareHint: "Share of revenue from first-time buyers.",
  returns: "Returns rate (optional)",
  returnsHint:
    "Roughly what share of sales came back as returns. Set to 0 to ignore.",
  resultPrompt: "Fill in your numbers to grade last Black Friday.",
  headlineProfit: "Estimated contribution",
  headlineLoss: "Estimated shortfall",
  overallLabel: "Overall",
  focusLabel: "Focus here next time",
  /** Small qualifier shown on the ad-efficiency row so the blended basis is
   * visible at a glance, not just buried in the disclaimer. */
  blendedQualifier: "blended",
  disclaimer:
    "Estimates only, based on the numbers you enter. Contribution is gross of fixed costs and tax, and uses blended ROAS across all revenue, which flatters paid performance. Returns are deducted at the rate you enter; retention and lifetime value aren't measured here.",
} as const;

/** Input ranges and step sizes. Inputs are clamped to these bounds. */
export const RANGES = {
  // Money fields accept a wide range; the ceiling just guards against overflow.
  money: { min: 0, max: 100_000_000, step: 1 },
  // Percentages that can't sensibly reach 100: a 100% margin needs zero cost,
  // and a 100% discount is a giveaway rather than a pricing decision.
  percent: { min: 0, max: 99, step: 1 },
  // New-customer share is a clean 0–100 share.
  share: { min: 0, max: 100, step: 1 },
  // Returns rate stops short of 100: a weekend where everything came back has
  // no revenue left to grade.
  returns: { min: 0, max: 90, step: 1 },
} as const;

/**
 * Neutral, round placeholder values the user is meant to change. Deliberately
 * not derived from any Wayflyer merchant data. They describe a healthy-but-
 * improvable weekend — a 55% margin discounted 25%, ad spend that clears its
 * break-even comfortably, and a new-customer share right on the ~45% benchmark
 * — so the card loads with a realistic mixed grade rather than all A's or a
 * loss. Returns default to a modest 10% — a realistic drag rather than the
 * flattering zero, which the user overrides with their own figure.
 */
export const DEFAULTS = {
  revenue: 60_000,
  marginPct: 55,
  discountPct: 25,
  adSpend: 12_000,
  newCustomerSharePct: 40,
  returnsPct: 10,
} as const;

/** The five grades, best to worst. Points drive the overall average. */
export type Grade = "A" | "B" | "C" | "D" | "F";

export const GRADE_POINTS: Record<Grade, number> = {
  A: 4,
  B: 3,
  C: 2,
  D: 1,
  F: 0,
};

/** Tailwind classes for each grade's chip, keyed off the theme's semantic
 * colours so the card reads at a glance: green good, amber middling, red bad. */
export const GRADE_CHIP: Record<Grade, string> = {
  A: "bg-green-100 text-green-800",
  B: "bg-green-50 text-green-600",
  C: "bg-orange-50 text-orange-600",
  D: "bg-orange-100 text-orange-800",
  F: "bg-red-50 text-red-700",
};

/**
 * Discount discipline: how much of the normal margin the discount consumed
 * (`discount / margin`). An F is reserved for selling at or below cost, handled
 * in the component before these bands apply. Bands are ordered best-first; the
 * first whose `maxConsumed` the ratio falls within wins.
 */
export const DISCOUNT_BANDS: { maxConsumed: number; grade: Grade }[] = [
  { maxConsumed: 0.3, grade: "A" },
  { maxConsumed: 0.5, grade: "B" },
  { maxConsumed: 0.7, grade: "C" },
  { maxConsumed: 1, grade: "D" },
];

/**
 * Ad efficiency: actual blended ROAS as a multiple of break-even ROAS
 * (`coverage`). Break-even is 1×; the 1.3–1.5× buffer is the common target for
 * real profit after ads. Below break-even the ads lost money on a contribution
 * basis. Bands are ordered best-first; the first whose `minCoverage` the value
 * meets or beats wins.
 */
export const AD_BANDS: { minCoverage: number; grade: Grade }[] = [
  { minCoverage: 1.5, grade: "A" },
  { minCoverage: 1.3, grade: "B" },
  { minCoverage: 1.0, grade: "C" },
  { minCoverage: 0.8, grade: "D" },
];

/**
 * Acquisition quality: new-customer share against the ~45% BFCM benchmark. A
 * higher share earns a better grade because using the promo to bring in new
 * buyers is its stated purpose — but the copy carries the retention caveat, as
 * Black-Friday-acquired shoppers repeat far less often. A low share means the
 * discount mostly went to existing customers who may have bought anyway. Bands
 * are ordered best-first; the first whose `minShare` the value meets wins.
 */
export const ACQUISITION_BANDS: { minShare: number; grade: Grade }[] = [
  { minShare: 45, grade: "A" },
  { minShare: 35, grade: "B" },
  { minShare: 25, grade: "C" },
  { minShare: 15, grade: "D" },
];

export interface FaqQuestion {
  question: string;
  answer: string;
}

export const FAQ_TITLE = "Black Friday report card FAQ";

export const FAQ_QUESTIONS: FaqQuestion[] = [
  {
    question: "What is a Black Friday report card?",
    answer:
      "It's a way to grade last year's Black Friday weekend on more than just revenue. Enter your revenue, margin, discount depth, ad spend and new-customer share, and the tool grades three things separately: discount discipline, ad efficiency and acquisition quality, then rolls them into one overall grade and flags the single area to fix first.",
  },
  {
    question: "What is a good ROAS for Black Friday?",
    answer:
      "There isn't one universal number, whatever a flat \"aim for 4x\" rule might suggest. The ROAS you need to be profitable depends entirely on your margin: a 60% margin brand might be fine at 2x, while a 30% margin brand can lose money at the same ratio. That's why this tool grades your ad spend against your own break-even ROAS rather than a generic benchmark.",
  },
  {
    question: "What is break-even ROAS and how does this tool calculate mine?",
    answer:
      'Break-even ROAS is the minimum return on ad spend needed just to cover what the ad brought in, given your margin and discount depth. It answers "how much do I need to make per unit of ad spend before I\'ve made any profit at all?" This tool works it out from your contribution margin and discount depth, then compares your actual blended ROAS against it to grade ad efficiency.',
  },
  {
    question: "What is blended ROAS, and why can it flatter your results?",
    answer:
      "Blended ROAS divides all your revenue (including organic and returning-customer sales that would have happened anyway) by your ad spend, rather than only the revenue your ads can be directly credited with. It's the number most brands actually have to hand, so it's what this tool uses, but it means a comfortable-looking ROAS can still hide ads that lost money on their own.",
  },
  {
    question: "How is my overall grade worked out?",
    answer:
      "Discount discipline, ad efficiency (if you entered ad spend) and acquisition quality are each graded A to F, then averaged and rounded to a single overall grade. **Focus here next time** always points at whichever of those graded areas is weakest, since that's the one lever most likely to move your result.",
  },
  {
    question: "Is my data saved anywhere?",
    answer:
      "No. Your figures stay in the browser tab. Once you've entered enough to produce a result, we log the grades and percentage inputs (margin, discount depth, new-customer share, returns) for our own product analytics; raw money figures like your revenue and ad spend are never sent.",
  },
  {
    question: "Where can I get help improving next year's numbers?",
    answer:
      "Check your margin maths for every planned discount with the [discount break-even calculator](/tools/discount-break-even), map your prep back from sale day with the [backwards planner](/tools/backwards-planner), or browse the [full Black Friday toolkit](/tools).",
  },
];
